import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CustomerModal, DetailsCard, ReactTable } from "components";
import { CUSTOMER_TYPES } from "models";
import { getCustomerTypeLabel } from "modules/Appointments/components/NewAppointment/utils";
import "modules/CarDetails/components/CustomerInfo/CustomerInfo.scss";
import { useCarDealer, useCarLocation, useCustomerInfoQuery } from "modules/CarDetails/hooks";

import type { SectionMenu } from "components";
import type { Customer } from "models";
import type { CarCustomerInfo } from "modules/CarDetails/hooks";

type CustomerInfoProps = {
  latestAppointmentId?: number;
  sectionMenu: SectionMenu;
  isExpanded: boolean;
};

type CustomerTableRow = {
  customerType: CUSTOMER_TYPES;
  customer: Customer;
  opted_out_from_importing: boolean;
};

const columnHelper = createColumnHelper<CustomerTableRow>();

const getCustomer = (customerInfo: CarCustomerInfo, showModalCustomerId: number | null) => {
  if (!showModalCustomerId) return;
  if (showModalCustomerId === customerInfo.owner?.id) return customerInfo.owner;
  if (showModalCustomerId === customerInfo.driver?.id) return customerInfo.driver;
  if (showModalCustomerId === customerInfo.contractor?.id) return customerInfo.contractor;

  return;
};

export const getCustomerInfoCount = (customerInfo?: CarCustomerInfo) =>
  (customerInfo?.contractor ? 1 : 0) + (customerInfo?.driver ? 1 : 0) + (customerInfo?.owner ? 1 : 0);

export const CustomerInfo = ({ latestAppointmentId, sectionMenu, isExpanded }: CustomerInfoProps) => {
  const { t } = useTranslation();

  const carDealer = useCarDealer();
  const carLocation = useCarLocation();

  const [showModalCustomerId, setShowModalCustomerId] = useState<number>(0);

  const { data: customerInfo, isFetching } = useCustomerInfoQuery(latestAppointmentId);

  if (!sectionMenu.count || !customerInfo) return null;

  const columns = [
    columnHelper.accessor(row => row.customerType, {
      id: "label",
      header: "",
      size: 80,
      cell: ({
        row: {
          original: { customerType }
        }
      }) => getCustomerTypeLabel(t, customerType)
    }),
    columnHelper.accessor(row => row, {
      id: "name",
      header: t("v8_name", "Name"),
      cell: ({
        row: {
          original: { customer, opted_out_from_importing }
        }
      }) => {
        if (opted_out_from_importing) return <span>{t("v8_customer_opted_out", "Customer opted out")}</span>;

        const customerName = `${customer.title || ""} ${customer.firstname || ""} ${customer.surname || ""}`.trim() || customer.company;

        return <span onClick={() => setShowModalCustomerId(customer.id)}>{customerName}</span>;
      }
    }),
    columnHelper.accessor(row => row, {
      id: "contact",
      header: t("v8_phone_number", "Phone Number"),
      cell: ({
        row: {
          original: { customer }
        }
      }) => customer.tel_business_nr || customer.tel_mobile_business || customer.tel_private_nr || customer.tel_mobile_private || ""
    }),
    columnHelper.accessor(row => row, {
      id: "email",
      header: t("v8_email", "Email"),
      cell: ({
        row: {
          original: { customer }
        }
      }) => customer.email_private || customer.email_business || ""
    })
  ];

  const tableRows: CustomerTableRow[] = [];
  if (customerInfo.owner)
    tableRows.push({
      customer: customerInfo.owner,
      customerType: CUSTOMER_TYPES.Owner,
      opted_out_from_importing: customerInfo.owner_opted_out_from_importing
    });

  if (customerInfo.driver)
    tableRows.push({
      customer: customerInfo.driver,
      customerType: CUSTOMER_TYPES.Driver,
      opted_out_from_importing: customerInfo.driver_opted_out_from_importing
    });

  if (customerInfo.contractor)
    tableRows.push({
      customer: customerInfo.contractor,
      customerType: CUSTOMER_TYPES.Contractor,
      opted_out_from_importing: customerInfo.contractor_opted_out_from_importing
    });

  const customer = getCustomer(customerInfo, showModalCustomerId);

  return (
    <DetailsCard isExpanded={isExpanded} sectionMenuId={sectionMenu.id} title={sectionMenu.title} icon={sectionMenu.icon}>
      <div className="CustomerInfo">
        <ReactTable isFetching={isFetching} columns={columns} data={tableRows} />
        {customer && <CustomerModal isOpen={true} onClose={() => setShowModalCustomerId(0)} customer={customer} dealer={carDealer} location={carLocation} />}
      </div>
    </DetailsCard>
  );
};
